import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, SESSION_EXPIRED_EVENT } from './api';

interface AuthState {
  loading: boolean;
  authenticated: boolean;
  email: string | null;
  profile: { voornaam?: string; achternaam?: string; roepnaam?: string | null } | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<AuthState, 'refresh' | 'logout'>>({
    loading: true,
    authenticated: false,
    email: null,
    profile: null,
  });

  const refresh = async () => {
    try {
      const data = await api.auth.me();
      setState({
        loading: false,
        authenticated: data.authenticated,
        email: data.email ?? null,
        profile: data.profile ?? null,
      });
    } catch {
      setState({ loading: false, authenticated: false, email: null, profile: null });
    }
  };

  const logout = async () => {
    await api.auth.logout();
    await refresh();
    window.location.href = '/';
  };

  useEffect(() => {
    refresh();
  }, []);

  // Sessie verlopen tijdens gebruik → markeer uitgelogd zodat App de LoginPage toont
  // (i.p.v. stille fouten). De gebruiker logt opnieuw in en is meteen weer terug.
  useEffect(() => {
    const onExpired = () => {
      setState((s) => (s.authenticated ? { ...s, authenticated: false } : s));
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  return <AuthContext.Provider value={{ ...state, refresh, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
